import { useEffect, useState } from 'react';
import { useDisabledFeatureFlags } from 'lib/hooks/useDisabledFeatureFlags';
import { getOneTrustActiveGroups, getOneTrustCookieActiveGroups } from 'lib/utils/onetrust-utils';

export type OneTrustGroup = '2' | '3' | '4';

export enum OneTrustDefaultConsentPreference {
  Consent = 'consent',
  NoConsent = 'noconsent',
}

export type OneTrustDefaultConsentPreferenceValue = `${OneTrustDefaultConsentPreference}`;

export interface UseOneTrustConsentResult {
  /** True when the required optanon groups are NOT all currently consented/active. */
  isBlocked: boolean;
  /** True once a consent decision (cookie, live active groups, or fallback default) has been resolved. */
  readyToMount: boolean;
  /** False when OneTrust is disabled via feature flag — consumers should render nothing/unfiltered. */
  oneTrustEnabled: boolean;
}

// Max time to wait for OneTrust's SDK to expose its admin-configured default
// group state before falling back to defaultConsentPref.
const MAX_DEFAULT_GROUPS_POLL_ATTEMPTS = 40;
const DEFAULT_GROUPS_POLL_INTERVAL_MS = 50;

/**
 * Shared OneTrust consent evaluation, used by any consumer that needs to gate
 * content behind a set of optanon consent groups (e.g. `OneTrustFilter`, rich
 * text video embeds). Honors the OneTrust admin-configured default toggle
 * state before a consent cookie exists, and falls back to `defaultConsentPref`
 * only if that default state isn't available yet.
 *
 * @param optanonGroups - Required consent groups. All must be active for `isBlocked` to be false.
 * @param defaultConsentPref - Fallback preference used only while the real default state is unavailable.
 * @param enabled - When false, skips all consent evaluation and reports unblocked/ready immediately.
 */
export function useOneTrustConsent(
  optanonGroups: OneTrustGroup[],
  defaultConsentPref: OneTrustDefaultConsentPreferenceValue = OneTrustDefaultConsentPreference.Consent,
  enabled = true
): UseOneTrustConsentResult {
  const defaultPref = defaultConsentPref === OneTrustDefaultConsentPreference.NoConsent;
  const [isBlocked, setBlocked] = useState(defaultPref);
  const [readyToMount, setReadyToMount] = useState(false);
  const disabledFeatures = useDisabledFeatureFlags();
  const oneTrustEnabled = !disabledFeatures?.OT;

  useEffect(() => {
    if (!enabled) {
      setBlocked(false);
      setReadyToMount(true);
      return;
    }

    if (!oneTrustEnabled || typeof window === 'undefined') {
      setBlocked(false);
      setReadyToMount(true);
      return;
    }

    let cancelled = false;

    const evaluateAgainst = (activeGroups: string[]) => {
      if (cancelled) return;
      // Compare active optanon groups vs selected groups
      const userActiveGroups = optanonGroups?.filter((group: OneTrustGroup) =>
        activeGroups.includes(group)
      );
      const groupsMismatch =
        userActiveGroups?.slice().sort().toString() !== optanonGroups.slice().sort().toString();

      setBlocked(groupsMismatch);
      setReadyToMount(true);
    };

    const cookieActiveGroups = getOneTrustCookieActiveGroups();

    if (cookieActiveGroups) {
      // User (or OneTrust) has already recorded a consent choice — trust the cookie.
      evaluateAgainst(cookieActiveGroups);
      return;
    }

    // No consent cookie yet. Rather than assuming a hardcoded default, honor whatever
    // default state OneTrust's admin console has configured for these categories
    // (sliders on/off). The SDK script loads beforeInteractive, so its default groups
    // are usually already available — use them immediately when present.
    const immediateActiveGroups = getOneTrustActiveGroups();
    if (immediateActiveGroups.length > 0) {
      evaluateAgainst(immediateActiveGroups);
      return;
    }

    // Not available yet — render using defaultConsentPref right away to avoid
    // delaying the initial paint, then keep polling briefly to correct the state
    // once OneTrust reports its real default groups.
    setBlocked(defaultPref);
    setReadyToMount(true);

    let attempts = 0;
    let pollTimeout: ReturnType<typeof setTimeout> | undefined;
    const pollForDefaultGroups = () => {
      if (cancelled) return;
      const activeGroups = getOneTrustActiveGroups();
      if (activeGroups.length > 0) {
        evaluateAgainst(activeGroups);
        return;
      }
      if (attempts >= MAX_DEFAULT_GROUPS_POLL_ATTEMPTS) return;
      attempts += 1;
      pollTimeout = setTimeout(pollForDefaultGroups, DEFAULT_GROUPS_POLL_INTERVAL_MS);
    };
    pollForDefaultGroups();

    return () => {
      cancelled = true;
      if (pollTimeout) clearTimeout(pollTimeout);
    };
  }, [defaultPref, oneTrustEnabled, optanonGroups, enabled]);

  return { isBlocked, readyToMount, oneTrustEnabled };
}
